// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin panel  (/admin/security — fingerprint sign-in)
// The signed-in staff member's fingerprints (WebAuthn passkeys, max 5): add this device's fingerprint (a platform authenticator with user
// verification — Touch ID, Windows Hello, Android biometrics) and remove ones they no longer use. Once one exists, "Sign in with fingerprint"
// on /welcome/staff works with no username. Enrolment needs an existing password sign-in (this page is behind the staff guard), so a
// fingerprint can never create an account. utils/staffAuth holds the WebAuthn plumbing.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useState } from "react";
import { CircleAlert, Fingerprint, Loader2, Trash2 } from "lucide-react";
import { ApiError } from "../../utils/api";
import { formatWhen } from "../../utils/orders";
import { deletePasskey, enrolFingerprint, fetchPasskeys, passkeysSupported, type Passkey } from "../../utils/staffAuth";

const MAX = 5;

export default function AdminSecurity() {
  const [keys, setKeys] = useState<Passkey[] | null>(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const supported = passkeysSupported();

  const load = useCallback(async () => {
    try {
      setKeys(await fetchPasskeys());
    } catch {
      setError("We couldn't load your fingerprints.");
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const add = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await enrolFingerprint(label.trim() || "This device");
      setLabel("");
      await load();
      setNotice("Fingerprint added. You can now sign in with it from the staff sign-in page.");
    } catch (caught) {
      setError(caught instanceof ApiError || caught instanceof Error ? caught.message : "We couldn't add your fingerprint.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (key: Passkey) => {
    setBusy(true);
    setError(null);
    try {
      await deletePasskey(key.id);
      setConfirming(null);
      await load();
      setNotice("Fingerprint removed.");
    } catch {
      setError("We couldn't remove that fingerprint.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <h1 className="font-display text-2xl font-bold">Security</h1>
      <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold"><Fingerprint className="h-5 w-5" aria-hidden="true" /> Fingerprint sign-in</h2>
        <p className="mt-1 text-sm text-ocean-800/90 dark:text-foam-100/85">Register this device&apos;s fingerprint (or face / PIN, whatever it uses to unlock) to sign in without typing your password. Your biometric never leaves the device.</p>
        <p role="status" className="mt-3 min-h-5 text-sm font-semibold text-teal-700 dark:text-teal-300">{notice}</p>
        <p role="alert" className="flex min-h-5 items-start gap-1.5 text-sm text-coral-600 dark:text-coral-300">{error && <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />}{error}</p>
        {supported ? (
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <div className="min-w-48 flex-1">
              <label htmlFor="pk-label" className="text-xs font-bold">Name this device</label>
              <input id="pk-label" maxLength={40} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Office laptop" className="mt-1 min-h-11 w-full rounded-xl bg-white px-3 text-sm text-ocean-950 ring-1 ring-ocean-900/20 focus-visible:outline-2 focus-visible:outline-teal-600 dark:bg-ocean-950 dark:text-foam-50" />
            </div>
            <button type="button" onClick={() => void add()} disabled={busy || (keys?.length ?? 0) >= MAX} className="inline-flex min-h-11 items-center gap-2 rounded-full bg-ocean-800 px-5 text-sm font-bold text-white disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Fingerprint className="h-4 w-4" aria-hidden="true" />} Add fingerprint
            </button>
          </div>
        ) : (
          <p className="mt-2 text-sm font-semibold">This browser doesn&apos;t support fingerprint sign-in.</p>
        )}
        {(keys?.length ?? 0) >= MAX && <p className="mt-2 text-xs">You have the maximum of {MAX}. Remove one to add another.</p>}
      </section>

      <section aria-labelledby="mine-h">
        <h2 id="mine-h" className="font-display text-lg font-bold">Your fingerprints</h2>
        {keys === null ? <p role="status" className="mt-2 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading…</p> : keys.length === 0 ? <p className="mt-2 text-sm">None yet.</p> : (
          <ul className="mt-2 flex flex-col gap-2">
            {keys.map((key) => (
              <li key={key.id} className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10" data-testid="passkey">
                <div className="flex items-center justify-between gap-2">
                  <div><p className="font-bold">{key.label}</p><p className="text-xs text-ocean-800/75 dark:text-foam-100/70">Added {formatWhen(key.createdAt)} · {key.lastUsedAt ? `last used ${formatWhen(key.lastUsedAt)}` : "never used"}</p></div>
                  {confirming !== key.id && <button type="button" onClick={() => setConfirming(key.id)} aria-label={`Remove ${key.label}`} className="flex h-11 w-11 items-center justify-center rounded-full bg-coral-500/10 text-coral-600 dark:text-coral-300"><Trash2 className="h-4 w-4" aria-hidden="true" /></button>}
                </div>
                {confirming === key.id && (
                  <div role="alertdialog" aria-label={`Remove ${key.label}?`} className="mt-2 rounded-xl bg-coral-500/10 p-3">
                    <p className="text-sm font-semibold">Remove this fingerprint? You can still sign in with your password.</p>
                    <div className="mt-2 flex gap-2">
                      <button type="button" disabled={busy} onClick={() => void remove(key)} className="min-h-11 rounded-full bg-coral-600 px-4 text-sm font-bold text-white disabled:opacity-60">Yes, remove</button>
                      <button type="button" onClick={() => setConfirming(null)} className="min-h-11 rounded-full bg-ocean-700/10 px-4 text-sm font-bold dark:bg-white/10">Keep it</button>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
